import React, { FC, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import showNotification from '../../../components/extras/showNotification';
import GoogleLoginButton from '../../../components/GoogleLoginButton';
import { API_BASE_URL } from '../../../local';

const Login: FC<{ isSignUp?: boolean }> = ({ isSignUp }) => {
	const { setUser } = useContext(AuthContext);
	const { darkModeStatus } = useDarkMode();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [loginError, setLoginError] = useState<string | null>(null);
	const navigate = useNavigate();

	// ── Redirect ke home kalo udah login ──
	useEffect(() => {
		const token = localStorage.getItem('token');
		if (token) {
			navigate('/', { replace: true });
		}
	}, [navigate]);
	const formik = useFormik({
		initialValues: {
			loginEmail: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginEmail?: string; loginPassword?: string } = {};
			if (!values.loginEmail) {
				errors.loginEmail = 'Required';
			}
			if (!values.loginPassword) {
				errors.loginPassword = 'Required';
			}
			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			setIsLoading(true);
			setLoginError(null); // Reset error

			const formData = new FormData();
			formData.append('email', values.loginEmail);
			formData.append('password', values.loginPassword);

			try {
				const response = await fetch(`${API_BASE_URL}/auth/login`, {
					method: 'POST',
					body: formData,
				});

				const data = await response.json();
				console.log('Response from API:', data); // Tambahkan log ini

				// Cek apakah login berhasil
				if (!data.success) {
					throw new Error(data.msg);
				}

				// Jika berhasil, ambil token dari respon API
				if (data.token) {
					// Backend sekarang return menus langsung (role-based)
					// Simpan menus ke localStorage (array flat dari menu items)
					const menus = data.menus || [];
					localStorage.setItem('menu', JSON.stringify(menus));

					if (setUser) {
						setUser(values.loginEmail);
					}

					localStorage.setItem('token', data.token);
					localStorage.setItem('userdata', JSON.stringify(data.cookiedata || data.user));
					// Role read-only (audit) — dipakai buat nyembunyiin aksi tulis di UI
					localStorage.setItem('is_read_only', data.user?.is_read_only ? 'true' : 'false');

					// Reload menu dari data.menus (role-based dari backend)
					const { reloadUserMenu } = await import('../../../menu');
					if (typeof reloadUserMenu === 'function') reloadUserMenu();

					showNotification('','Login successful!!','info');
					// Full page reload — pastiin semua context (Branch, Auth) ready
					window.location.href = '/';
				}
			} catch (error) {
				const errorMessage = (error as Error).message || 'An error occurred.'; // Casting error ke tipe Error
				setLoginError(errorMessage);
			} finally {
				setIsLoading(false);
			}
		}
	});

	return (
		<>
			{/* ── Animation keyframes ── */}
			<style>{`
				@keyframes fadeSlideUp {
					0% {
						opacity: 0;
						transform: translateY(12px);
					}
					100% {
						opacity: 1;
						transform: translateY(0);
					}
				}
			`}</style>
			<PageWrapper
				isProtected={false}
				title='Login'
				className={classNames({ 'bg-dark': true })}
			>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-4'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none fw-bold display-2',
											{ 'text-dark': !darkModeStatus, 'text-light': darkModeStatus }
										)}
										aria-label='Facit'
									>
										<Logo width={200} />
									</Link>
									{/* ── Title: LEGAL APPS ── */}
									<div className='mt-3' style={{ lineHeight: 1.15 }}>
										<div
											style={{
												fontSize: '1.5rem',
												fontWeight: 700,
												letterSpacing: '0.15em',
												color: '#04924b',
												textTransform: 'uppercase',
												animation: 'fadeSlideUp 0.6s ease-out 0.2s both',
											}}
										>
											LEGAL
										</div>
										<div
											style={{
												fontSize: '1rem',
												fontWeight: 300,
												letterSpacing: '0.4em',
												background: 'linear-gradient(135deg, #63b43d 0%, #cbdb54 50%, #04924b 100%)',
												WebkitBackgroundClip: 'text',
												WebkitTextFillColor: 'transparent',
												backgroundClip: 'text',
												textTransform: 'uppercase',
												animation: 'fadeSlideUp 0.6s ease-out 0.5s both',
											}}
										>
											APPS
										</div>
									</div>
								</div>
								<form className='row g-4' onSubmit={formik.handleSubmit}>
									<div className='col-12'>
										<FormGroup
											id='loginEmail'
											isFloating
											label='Your email'
										>
											<Input
												autoComplete='email'
												value={formik.values.loginEmail}
												isTouched={formik.touched.loginEmail}
												invalidFeedback={formik.errors.loginEmail}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										<FormGroup
											id='loginPassword'
											isFloating
											label='Password'
										>
											<Input
												type='password'
												autoComplete='current-password'
												value={formik.values.loginPassword}
												isTouched={formik.touched.loginPassword}
												invalidFeedback={formik.errors.loginPassword}
												validFeedback='Looks good!'
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
											/>
										</FormGroup>
									</div>
									{loginError && (
										<div className='col-12 text-danger'>{loginError}</div>
									)}
									<div className='col-12'>
										<Button
											type= 'submit'
											color='warning'
											className='w-100 py-3'
											isDisable={!formik.values.loginEmail || !formik.values.loginPassword}
										>
											{isLoading && <Spinner isSmall inButton isGrow />}
											Continue
									</Button>
								</div>

									{/* ── Separator ── */}
									<div className='col-12'>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '12px',
												margin: '4px 0',
											}}
										>
											<div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
											<span
												style={{
													fontSize: '0.8rem',
													color: '#9ca3af',
													fontWeight: 500,
												}}
											>
												atau login dengan
											</span>
											<div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
										</div>
									</div>

									{/* ── Google Sign-In ── */}
									<div className='col-12'>
										<GoogleLoginButton
											onSuccess={async (idToken) => {
												setIsLoading(true);
												setLoginError(null);

												try {
													const response = await fetch(`${API_BASE_URL}/auth/google`, {
														method: 'POST',
														headers: { 'Content-Type': 'application/json' },
														body: JSON.stringify({ idToken }),
													});

													const data = await response.json();

													if (!data.success) {
														throw new Error(data.msg);
													}

													if (data.token) {
														const menus = data.menus || [];
														localStorage.setItem('menu', JSON.stringify(menus));

														if (setUser) {
															setUser(data.user?.email || '');
														}

												localStorage.setItem('token', data.token);
												localStorage.setItem('userdata', JSON.stringify(data.cookiedata || data.user));
												// Role read-only (audit) — dipakai buat nyembunyiin aksi tulis di UI
												localStorage.setItem('is_read_only', data.user?.is_read_only ? 'true' : 'false');

												const { reloadUserMenu } = await import('../../../menu');
												if (typeof reloadUserMenu === 'function') reloadUserMenu();

												showNotification('', 'Login Google berhasil!!', 'success');
														window.location.href = '/';
													}
												} catch (error) {
													const errorMessage = (error as Error).message || 'Google login gagal';
													setLoginError(errorMessage);
													showNotification('Error', errorMessage, 'danger');
												} finally {
													setIsLoading(false);
												}
											}}
											disabled={isLoading}
										/>
									</div>
							</form>
							</CardBody>
						</Card>
						<div className={classNames('text-center text-decoration-none me-3', { 'link-light': true })}
						>
							Dompet Dhuafa Enterprise System
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper>
		</>
	);
};

export default Login;
